import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PriceInputDirective } from '@ose/directives';
import { PasswordValidator } from '@ose/validators';

export type DropdownAlign = 'inline-start' | 'inline-end';

export type InputMode =
  | 'none'
  | 'text'
  | 'decimal'
  | 'numeric'
  | 'tel'
  | 'search'
  | 'email'
  | 'url';

@Component({
  selector: 'lib-input',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PriceInputDirective,
    TranslateModule,
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: InputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: InputComponent,
    },
  ],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class InputComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() inputType!: InputTypes;
  @Input() label!: string;
  @Input() showLabel = true;
  @Input() placeholder?: string;
  @Input() showPlaceHolder = true;
  @Input() mask?: string;

  @Input() inputClass?: string;
  @Input() controlClass?: string;
  @Input() labelClass?: string;
  @Input() groupTextClass?: string;

  @Input() dropdownToggleClass?: string;
  @Input() dropdownMenuClass?: string;

  @Input() group?: string;
  @Input() iconClass?: string;
  @Input() autocomplete?: string;
  @Input() showErrorPassword = false;
  @Input() inputValue?: string;

  @Input() inputMode?: InputMode;

  @Input('readonly')
  isReadonly = false;

  @Input()
  dropdownAlign: DropdownAlign = 'inline-start';

  @Input()
  items?: any[];

  @Input()
  valueKey?: string;

  @Input()
  labelKey?: string;

  @Output()
  valueChange = new EventEmitter<any>();

  @ContentChild('inputTemplate')
  inputTemplate?: TemplateRef<unknown>;

  @ContentChild('selectedItemTemplate')
  selectedItemTemplate?: TemplateRef<any>;

  @ContentChild('itemTemplate')
  itemTemplate?: TemplateRef<any>;

  inputId = 'input-' + Math.random().toString(36).substring(2, 10);

  dropdownOpen = false;

  protected myControl?: AbstractControl;
  protected isRequired?: boolean;

  selectedCountryCode = '+98';
  phoneNumber = '';

  value = signal<any>('');
  touched = signal(false);
  disabled = signal(false);

  inpType?: string;
  IconPassword = false;

  ngOnInit(): void {
    this.inpType = this.inputType === 'price' ? 'text' : this.inputType;
  }

  onChange = (value: any) => {};

  onTouched = () => {};

  onValidate = () => {};

  showPassword(): void {
    this.IconPassword = !this.IconPassword;

    this.inpType = this.IconPassword ? 'text' : 'password';
  }

  onSwitchChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.value.set(checked);

    this.onChange(checked);

    this.valueChange.emit(checked);

    this.touched.set(true);

    this.onTouched();
    this.onValidate();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    if (this.inputType === 'phone') {
      const phoneValue = obj === null || obj === undefined ? '' : String(obj);

      this.phoneNumber = phoneValue;

      this.value.set(phoneValue);

      return;
    }

    if (this.inputType === 'price') {
      if (obj === null || obj === undefined || obj === '') {
        this.value.set('');
      } else {
        const formatted = String(obj).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        this.value.set(formatted);
      }

      return;
    }

    if (this.mask) {
      const rawValue = this.getMaskRawValue(obj);

      this.value.set(this.applyMask(rawValue));

      return;
    }

    this.value.set(obj ?? '');
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  toggleDropdown(): void {
    if (this.disabled()) {
      return;
    }

    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  onIranPhoneInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value.replace(/\D/g, '');

    this.phoneNumber = input;

    this.value.set(input);

    this.onChange(input);

    this.valueChange.emit(input);

    this.onValidate();
  }

  selectItem(item: any): void {
    if (this.disabled()) {
      return;
    }

    const key = this.valueKey || 'id';

    const selectedValue = item[key];

    this.value.set(selectedValue);

    this.onChange(selectedValue);

    this.valueChange.emit(selectedValue);

    this.touched.set(true);

    this.onTouched();
    this.onValidate();

    this.dropdownOpen = false;
  }

  getSelectedItem(): any | undefined {
    const key = this.valueKey || 'id';

    const currentValue = this.value();

    if (
      currentValue === null ||
      currentValue === undefined ||
      currentValue === ''
    ) {
      return undefined;
    }

    return this.items?.find(
      (item) => String(item[key]) === String(currentValue)
    );
  }

  isItemSelected(item: any): boolean {
    const key = this.valueKey || 'id';

    return String(item[key]) === String(this.value());
  }

  getLabelByValue(value: any): string {
    const valueProperty = this.valueKey || 'id';

    const labelProperty = this.labelKey || 'name';

    const foundItem = this.items?.find((item) => item[valueProperty] === value);

    return foundItem?.[labelProperty] ?? '';
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidate = fn;
  }
  private getMaskRawValue(value: unknown): string {
    if (!this.mask) {
      return String(value ?? '');
    }

    const maxLength = this.getMaskLength();

    return String(value ?? '')
      .replace(/\D/g, '')
      .slice(0, maxLength);
  }

  private getMaskLength(): number {
    if (!this.mask) {
      return 0;
    }

    return Array.from(this.mask).filter((character) => character === 'X')
      .length;
  }

  private applyMask(rawValue: string): string {
    if (!this.mask) {
      return rawValue;
    }

    let valueIndex = 0;

    return Array.from(this.mask)
      .map((character) => {
        if (character !== 'X') {
          return character;
        }

        if (valueIndex < rawValue.length) {
          const value = rawValue[valueIndex];

          valueIndex += 1;

          return value;
        }

        return 'X';
      })
      .join('');
  }

  private setMaskCaretPosition(
    input: HTMLInputElement,
    valueLength: number
  ): void {
    if (!this.mask) {
      return;
    }

    let position = 0;

    if (valueLength > 0) {
      let filledCount = 0;

      for (let i = 0; i < this.mask.length; i += 1) {
        if (this.mask[i] !== 'X') {
          continue;
        }

        filledCount += 1;

        if (filledCount === valueLength) {
          position = i + 1;

          break;
        }
      }
    }

    requestAnimationFrame(() => {
      input.setSelectionRange(position, position);
    });
  }
  protected isMaskEmpty(): boolean {
    if (!this.mask) {
      return false;
    }

    return !/\d/.test(String(this.value() ?? ''));
  }
  validate(control: AbstractControl): ValidationErrors | null {
    this.myControl = control;

    this.isRequired = control.hasValidator(Validators.required);

    if (
      this.isRequired &&
      (!control.value || control.value.toString().trim() === '')
    ) {
      return {
        required: true,
      };
    }

    if (this.inputType === 'phone' && control.value) {
      const pattern = /^09\d{9}$/;

      if (!pattern.test(control.value)) {
        return {
          pattern: true,
        };
      }
    }

    if (this.inputType === 'password') {
      return PasswordValidator(control);
    }

    return null;
  }

  protected onInput(e: Event): void {
    const inputElement = e.target as HTMLInputElement;

    if (this.mask) {
      const rawValue = this.getMaskRawValue(inputElement.value);

      const maskedValue = this.applyMask(rawValue);

      this.value.set(maskedValue);

      inputElement.value = maskedValue;

      this.onChange(rawValue);

      this.valueChange.emit(rawValue);

      this.setMaskCaretPosition(inputElement, rawValue.length);

      return;
    }

    const rawValue = inputElement.value.replace(/,/g, '');

    this.value.set(inputElement.value);

    this.onChange(rawValue);

    this.valueChange.emit(rawValue);
  }

  protected onBlur(): void {
    this.touched.set(true);

    this.onTouched();
  }
}

export type InputTypes =
  | 'email'
  | 'password'
  | 'text'
  | 'number'
  | 'price'
  | 'text-area'
  | 'dropdown'
  | 'switch'
  | 'phone';
